/**
 * Ürün fotoğrafını YÜKLEMEDEN ÖNCE 150 KB'ın altına indir (canvas ile, ek
 * bağımlılık yok).
 *
 * HEDEF: nihai dosya en fazla PHOTO_TARGET_BYTES (150 KB) ve uzun kenar en
 * fazla MAX_EDGE (1280 px). Telefon kameraları 4000x3000 / 4-8 MB dosya
 * üretiyor; ürün kartında küçük bir görsel gösterildiği için bu boyutu
 * saklamak gereksiz.
 *
 * KALİTEYİ KORUMA STRATEJİSİ (kademeli): önce TAM çözünürlükte (1280) yüksek
 * kaliteyle denenir; hedefe girmezse kalite adım adım düşürülür. Kalite tabanı
 * da yetmezse çözünürlük bir kademe küçültülüp aynı merdiven tekrarlanır.
 * Böylece "hedefe giren EN YÜKSEK kalite" seçilir — sabit tek bir kalite
 * değeriyle ya gereksiz kayıp yaşanır ya da hedef tutturulamaz.
 *
 * FORMAT: JPEG. WebP daha iyi sıkıştırır ama `canvas.toBlob(..., "image/webp")`
 * eski Safari'de SESSİZCE PNG üretir; o durumda hem 150 KB aşılır hem de
 * backend'in imza kontrolü (declared=image/webp, gerçek=PNG) yüklemeyi
 * reddeder. JPEG toBlob her tarayıcıda desteklenir ve backend'in mevcut
 * allowlist'i (jpeg/png/webp) ile uyumludur → mevcut yapı bozulmaz.
 *
 * Sunucu tarafı doğrulama BUNA GÜVENMEZ: 150 KB hard limit ve MIME/imza
 * kontrolü backend'de yeniden uygulanır (backend/media.py). Buradaki küçültme
 * kullanıcı deneyimi içindir.
 *
 * Herhangi bir adım başarısız olursa ORİJİNAL dosya döndürülür; karar
 * backend'e kalır (anlaşılır bir 413 mesajı döner).
 */

/** Backend'deki MAX_PHOTO_BYTES ile AYNI olmalı (backend/media.py). */
export const PHOTO_TARGET_BYTES = 150 * 1024;

/** Uzun kenar üst sınırı. */
export const MAX_EDGE = 1280;

/** Çözünürlük kademeleri: hedef tutmazsa sırayla küçültülür. */
export const EDGE_STEPS = [1280, 1024, 800, 640];

/**
 * Her çözünürlük kademesinde denenecek JPEG kaliteleri (yüksekten düşüğe).
 * İlk kademede daha geniş bir aralık taranır çünkü asıl amaç 1280 px'i
 * korumak; küçültmeye ancak kalite tabanı yetmezse inilir.
 */
export const QUALITY_STEPS = {
  1280: [0.85, 0.75, 0.65, 0.55, 0.45],
  1024: [0.75, 0.6, 0.48],
  800: [0.7, 0.55, 0.45],
  640: [0.65, 0.5, 0.4],
};

// ---------------------------------------------------------------------------
// Saf yardımcılar (DOM gerektirmez → test edilebilir)
// ---------------------------------------------------------------------------

/** Verilen boyutu `maxEdge` karesine sığdır. Büyütme YAPMAZ. */
export function fitWithin(width, height, maxEdge = MAX_EDGE) {
  const w0 = Math.max(1, Math.round(width || 0));
  const h0 = Math.max(1, Math.round(height || 0));
  const scale = Math.min(1, maxEdge / Math.max(w0, h0));
  return {
    width: Math.max(1, Math.round(w0 * scale)),
    height: Math.max(1, Math.round(h0 * scale)),
    scale,
  };
}

/** Dosya hedef bütçenin içinde mi? */
export function isWithinBudget(size) {
  return Number(size) > 0 && Number(size) <= PHOTO_TARGET_BYTES;
}

/**
 * Bu dosyaya hiç dokunmadan geçebilir miyiz?
 * Yalnızca HEM boyut bütçedeyse HEM de çözünürlük sınırın içindeyse.
 */
export function needsRecompress(size, width, height) {
  if (!isWithinBudget(size)) return true;
  return Math.max(width || 0, height || 0) > MAX_EDGE;
}

/**
 * Denenecek (kenar, kalite) çiftlerini sırayla üret.
 * `sourceEdge`: kaynağın uzun kenarı — kaynaktan büyük kademeler atlanır
 * (küçük bir fotoğrafı büyütüp yeniden kodlamanın anlamı yok).
 */
export function buildAttempts(sourceEdge = MAX_EDGE) {
  const attempts = [];
  const effective = EDGE_STEPS.filter((e) => e <= sourceEdge);
  // Kaynak tüm kademelerden küçükse en küçük kademeyi kullan (kaynak boyutunda
  // kalınır, yalnızca kalite düşürülür).
  const steps = effective.length ? effective : [EDGE_STEPS[EDGE_STEPS.length - 1]];
  for (const edge of steps) {
    for (const quality of QUALITY_STEPS[edge] || QUALITY_STEPS[640]) {
      attempts.push({ edge, quality });
    }
  }
  return attempts;
}

// ---------------------------------------------------------------------------
// DOM tarafı
// ---------------------------------------------------------------------------
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function drawToCanvas(img, edge) {
  const { width, height } = fitWithin(img.width, img.height, edge);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  // JPEG şeffaflığı desteklemediği için beyaz zemin (PNG girdilerde şart).
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

function encode(canvas, quality) {
  return new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality)
  );
}

function toJpegFile(blob, originalName) {
  const name = (originalName || "foto").replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
}

/**
 * Fotoğrafı 150 KB / 1280 px sınırlarına indir.
 *
 * Dönen değer daima bir File'dır. Hedefe ulaşılamazsa ulaşılan EN KÜÇÜK sonuç
 * döner (backend gerekirse reddeder) — sessizce büyük dosya göndermeyiz.
 */
export async function compressImage(file) {
  if (!file || !file.type?.startsWith("image/")) return file;

  try {
    const img = await loadImage(file);

    // Zaten bütçede ve sınır içindeyse yeniden kodlamaya gerek yok →
    // gereksiz kalite kaybı olmaz.
    if (!needsRecompress(file.size, img.width, img.height)) return file;

    const sourceEdge = Math.max(img.width, img.height);
    let best = null;
    // Aynı kenar için canvas'ı bir kez çiz, tüm kaliteleri onun üzerinde dene.
    let canvas = null;
    let canvasEdge = null;

    for (const { edge, quality } of buildAttempts(sourceEdge)) {
      if (canvasEdge !== edge) {
        canvas = drawToCanvas(img, edge);
        if (!canvas) return file; // canvas yok (ör. jsdom) → dokunma
        canvasEdge = edge;
      }
      const blob = await encode(canvas, quality);
      if (!blob) continue;
      if (!best || blob.size < best.size) best = blob;
      if (blob.size <= PHOTO_TARGET_BYTES) {
        // Hedefe giren İLK sonuç, aynı zamanda en yüksek kaliteli olandır.
        return toJpegFile(blob, file.name);
      }
    }

    if (!best) return file;
    // Hedefe girilemedi: en küçüğü döndür, ama orijinalden büyükse orijinali
    // koru (backend her iki durumda da 150 KB üstünü reddeder).
    if (best.size >= file.size) return file;
    return toJpegFile(best, file.name);
  } catch {
    return file;
  }
}
